import { MutationError } from '../../shared/ui/mutation-error';
import { PanelFoot, SectionPanel } from '../../shared/ui/section-panel';
import { Switch } from '../../shared/ui/switch';
import { Table, type IColumn } from '../../shared/ui/table';
import { Tooltip } from '../../shared/ui/tooltip';
import { ErrorState, ForbiddenState, LoadingState } from '../../shared/ui/states';
import { ApiError } from '../../shared/api/client';
import {
  cellFor,
  channelColumns,
  channelLabel,
  orgCellBlock,
  unwiredChannels,
  type TNotificationCategoryRow,
  type TNotificationChannel,
} from '../../entities/notification';
import { useNotificationRules } from './use-notification-rules';

interface ICellProps {
  category: TNotificationCategoryRow;
  channel: TNotificationChannel;
  onToggle: (category: string, channel: TNotificationChannel, enabled: boolean) => void;
  editable: boolean;
  saving: boolean;
}

const Cell = ({ category, channel, onToggle, editable, saving }: ICellProps) => {
  const cell = cellFor(category, channel);
  const blocked = orgCellBlock(cell) ?? (editable ? null : 'Only an owner can change this.');

  const control = (
    <span className="flex w-full">
      <Switch
        size="sm"
        checked={(cell?.availableToOrg && cell.orgEnabled) ?? false}
        disabled={blocked !== null || saving}
        aria-label={`${category.label} - ${channelLabel(channel)}`}
        onCheckedChange={(next) => onToggle(category.category, channel, next)}
      />
    </span>
  );

  return blocked === null ? control : <Tooltip content={blocked}>{control}</Tooltip>;
};

const listWords = (items: string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

// Email waits on the installation's mail server; Slack and Webhook have no sender yet at all,
// so there is no settings screen to link to — the sentence says who can change it instead.
const unwiredWords = (unwired: string[]): string => {
  if (unwired.length === 0) return 'In-app notifications always arrive, whatever else is on.';
  const why = [
    unwired.includes('Email') &&
      'email needs a mail server set up by whoever runs hookpipe for you',
    unwired.some((channel) => channel !== 'Email') &&
      `${listWords(unwired.filter((channel) => channel !== 'Email'))} can’t send notifications yet`,
  ].filter(Boolean);
  return `${listWords(unwired)} ${unwired.length === 1 ? 'isn’t' : 'aren’t'} available here, so ${unwired.length === 1 ? 'its switches stay' : 'those switches stay'} off: ${why.join('; ')}. In-app notifications always arrive.`;
};

export const NotificationRulesPanel = ({ editable }: { editable: boolean }) => {
  const rules = useNotificationRules();

  if (rules.settings.isError) {
    const error = rules.settings.error;
    if (error instanceof ApiError && error.status === 403) return <ForbiddenState needs="owner" />;
    return <ErrorState error={error} onRetry={() => void rules.settings.refetch()} />;
  }
  if (rules.settings.isPending) return <LoadingState rows={6} />;

  const { categories } = rules.settings.data;
  const channels = channelColumns(categories);
  const unwired = unwiredChannels(rules.settings.data);

  const columns: IColumn<TNotificationCategoryRow>[] = [
    {
      key: 'category',
      header: 'When',
      width: 'grow',
      ui: true,
      render: (category) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-primary">{category.label}</span>
          <span className="truncate text-meta text-muted" title={category.description}>
            {category.description}
          </span>
        </span>
      ),
    },
    ...channels.map((channel): IColumn<TNotificationCategoryRow> => ({
      key: channel,
      header: channelLabel(channel),
      width: 'w-col-when',
      ui: true,
      render: (category) => (
        <Cell
          category={category}
          channel={channel}
          onToggle={rules.toggle}
          editable={editable}
          saving={rules.save.isPending}
        />
      ),
    })),
  ];

  return (
    <SectionPanel
      title="For everyone in the organization"
      note={editable ? 'only owners change these' : 'set by an owner'}
    >
      {rules.save.isError && (
        <div className="px-card py-3">
          <MutationError error={rules.save.error} />
        </div>
      )}
      <Table
        tall
        layoutId="settings-notification-rules"
        columns={columns}
        rows={categories}
        rowKey={(category) => category.category}
      />
      <PanelFoot>{unwiredWords(unwired)}</PanelFoot>
    </SectionPanel>
  );
};
